<script lang="ts">
  import { t, lang as langState } from '../lang-state.svelte.js';

  interface Props {
    groupSize: number;
    targetStatus: 'none' | 'burning' | 'bleeding' | 'shocked' | 'poisoned' | 'blinded';
    targetPulsed: boolean;
    targetMarked: boolean;
    targetBleeding: boolean;
    firstShotMode: boolean;
    harrierPerfectCount: number;
    ravenousActive: boolean;
    weaponId: string | null;
    fullArmor: boolean;
    onGroupSize: (n: number) => void;
    onStatus: (s: Props['targetStatus']) => void;
    onPulsed: (v: boolean) => void;
    onMarked: (v: boolean) => void;
    onBleeding: (v: boolean) => void;
    onFirstShotMode: (v: boolean) => void;
    onHarrierPerfectCount: (n: number) => void;
    onRavenousActive: (v: boolean) => void;
    onFullArmor: (v: boolean) => void;
  }

  let { groupSize, targetStatus, targetPulsed, targetMarked, targetBleeding, firstShotMode, harrierPerfectCount, ravenousActive, weaponId, fullArmor, onGroupSize, onStatus, onPulsed, onMarked, onBleeding, onFirstShotMode, onHarrierPerfectCount, onRavenousActive, onFullArmor }: Props = $props();
  let lang = $derived(langState.current);

  const STATUSES: Array<{ id: Props['targetStatus']; en: string; ru: string; color: string; icon: string }> = [
    { id: 'none', en: 'None', ru: 'Нет', color: '#666', icon: '—' },
    { id: 'burning', en: 'Burning', ru: 'Горит', color: '#ff6a00', icon: '🔥' },
    { id: 'bleeding', en: 'Bleeding', ru: 'Кровотеч.', color: '#ef5350', icon: '🩸' },
    { id: 'shocked', en: 'Shocked', ru: 'Шок', color: '#42a5f5', icon: '⚡' },
    { id: 'poisoned', en: 'Poisoned', ru: 'Отравл.', color: '#4caf50', icon: '☠' },
    { id: 'blinded', en: 'Blinded', ru: 'Слеп', color: '#ffd84a', icon: '✦' },
  ];

  function statusLabel(s: typeof STATUSES[number]) {
    void lang;
    return lang === 'ru' ? s.ru : s.en;
  }
</script>

<section class="panel cond">
  <div class="panel-title"><span>{lang === 'ru' ? 'Условия боя' : 'Combat Conditions'}</span></div>

  <div class="row">
    <div class="label">{lang === 'ru' ? 'Группа' : 'Group size'}</div>
    <div class="group-btns">
      {#each [1, 2, 3, 4] as n (n)}
        <button
          class="btn small"
          class:active={groupSize === n}
          onclick={() => onGroupSize(n)}
        >{n === 1 ? (lang === 'ru' ? 'Соло' : 'Solo') : `${n}`}</button>
      {/each}
    </div>
    {#if groupSize > 1}
      <span class="hint-note">{lang === 'ru' ? 'TTK: HP врагов растёт' : 'TTK: enemy HP scales'}</span>
    {/if}
  </div>

  <div class="row">
    <div class="label">{lang === 'ru' ? 'Статус цели' : 'Target status'}</div>
    <div class="status-btns">
      {#each STATUSES as s (s.id)}
        <button
          class="status-btn"
          class:active={targetStatus === s.id}
          style:--sc={s.color}
          onclick={() => onStatus(s.id)}
        >
          <span class="si">{s.icon}</span>
          <span>{statusLabel(s)}</span>
        </button>
      {/each}
    </div>
  </div>

  <div class="row toggles">
    <label class="tog">
      <input type="checkbox" checked={targetPulsed} onchange={(e) => onPulsed((e.currentTarget as HTMLInputElement).checked)} />
      <span>{lang === 'ru' ? 'Цель отмечена (Pulse)' : 'Target pulsed'}</span>
    </label>
    <label class="tog" title={lang === 'ru' ? 'Метка от Negotiator’s Dilemma и т.п.' : 'Negotiator’s Dilemma mark and similar'}>
      <input type="checkbox" checked={targetMarked} onchange={(e) => onMarked((e.currentTarget as HTMLInputElement).checked)} />
      <span>{lang === 'ru' ? 'Цель помечена (Mark)' : 'Target marked'}</span>
    </label>
    <label class="tog" title={lang === 'ru' ? 'Кровотечение (Sadist / Perfect Sadist amp WD; auto когда в билде Caduceus / Regulus / Ongoing Directive 4pc).' : 'Bleeding target (gates Sadist / Perfect Sadist amp WD; auto-on with Caduceus / Regulus / Ongoing Directive 4pc).'}>
      <input type="checkbox" checked={targetBleeding} onchange={(e) => onBleeding((e.currentTarget as HTMLInputElement).checked)} />
      <span>{lang === 'ru' ? 'Цель кровоточит (Bleed)' : 'Target bleeding'}</span>
    </label>
    <label class="tog">
      <input type="checkbox" checked={fullArmor} onchange={(e) => onFullArmor((e.currentTarget as HTMLInputElement).checked)} />
      <span>{lang === 'ru' ? 'Полная броня' : 'Full armor'}</span>
    </label>
    <label class="tog" title={lang === 'ru' ? 'Показывать пик одиночного выстрела (Dodge City / Centurion). Выкл = усреднение по магазину для sustained DPS.' : 'Show peak on single-shot procs (Dodge City / Centurion). Off = averaged across magazine for sustained DPS.'}>
      <input type="checkbox" checked={firstShotMode} onchange={(e) => onFirstShotMode((e.currentTarget as HTMLInputElement).checked)} />
      <span>{lang === 'ru' ? 'First-shot peak' : 'First-shot peak'}</span>
    </label>
  </div>

  <div class="row">
    <div class="label" title={lang === 'ru' ? 'Активно только если в билде Harrier Pride. Шкала +10% WD за каждый Perfect-талант (макс 3 = +30%).' : 'Only relevant if Harrier Pride is equipped. Scales +10% WD per Perfect talent (max 3 = +30%).'}>{lang === 'ru' ? 'Harrier Perfect' : 'Harrier Perfect'}</div>
    <div class="group-btns">
      {#each [0, 1, 2, 3] as n (n)}
        <button class="btn small" class:active={harrierPerfectCount === n} onclick={() => onHarrierPerfectCount(n)}>{n}</button>
      {/each}
    </div>
    <span class="hint-note">×{harrierPerfectCount}/3</span>
  </div>

  {#if weaponId === 'the_ravenous'}
    <div class="row toggles">
      <label class="tog" title={lang === 'ru' ? 'Geri & Freki proc активен. Выкл = sustained DPS без бафа (между процами).' : 'Geri & Freki proc up. Off = sustained DPS between procs.'}>
        <input type="checkbox" checked={ravenousActive} onchange={(e) => onRavenousActive((e.currentTarget as HTMLInputElement).checked)} />
        <span>{lang === 'ru' ? 'Ravenous proc активен' : 'Ravenous proc active'}</span>
      </label>
    </div>
  {/if}
</section>

<style>
  .cond { margin-top: 8px; }
  .row { display: flex; align-items: center; gap: 8px; margin-bottom: 8px; flex-wrap: wrap; }
  .label { font: 700 10px/1 var(--f-display); letter-spacing: .12em; text-transform: uppercase; color: var(--orange); min-width: 90px; }
  .group-btns { display: flex; gap: 3px; }
  .status-btns { display: flex; gap: 3px; flex-wrap: wrap; }
  .status-btn {
    display: flex; align-items: center; gap: 4px;
    padding: 4px 8px;
    background: var(--bg-2); border: 1px solid var(--border);
    color: var(--text-dim);
    border-radius: var(--r-sm);
    cursor: pointer;
    font: 700 10px/1 var(--f-display); letter-spacing: .06em;
    text-transform: uppercase;
  }
  .status-btn:hover { border-color: var(--border-hi); }
  .status-btn.active {
    border-color: var(--sc, var(--orange));
    background: color-mix(in oklab, var(--sc) 15%, var(--bg-2));
    color: var(--sc, var(--orange));
  }
  .si { font-size: 12px; }
  .toggles { gap: 16px; }
  .tog { display: flex; align-items: center; gap: 6px; cursor: pointer; font: 700 10px/1 var(--f-display); letter-spacing: .1em; text-transform: uppercase; color: var(--text-dim); }
  .tog input { accent-color: var(--orange); }
  .hint-green { font: 700 10px/1 var(--f-mono); color: var(--set); padding: 4px 8px; background: rgba(61,166,120,.1); border-radius: 999px; }
  .hint-note { font: 500 10px/1 var(--f-mono); color: var(--muted); padding: 4px 8px; background: var(--raised); border-radius: 999px; font-style: italic; }
</style>
